'use client';

import type { PendingStimulus } from '@/domain/resolution';
import type { Command } from '@/domain/types';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * The device's own storage for the sync engine (docs/SYNC.md §7.2, §7.4): the
 * item records the device holds, what each surface last showed, the last cursor
 * per surface, and the command queue. One IndexedDB database, opened lazily,
 * behind an adapter that is a no-op wherever IndexedDB is absent (the server
 * render, a vitest run, a private window that refuses it) - every caller treats
 * persistence as best effort, so the in-memory store stays the truth of the
 * session and the database is only how it survives a reload.
 *
 * What the device NEVER stores is the JUST-TICKED RULE (docs/SYNC.md §7.3): the
 * rows someone else closed while they stood on your screen are a fact about
 * this tab's sitting, not about the graph. They are born when the surface
 * mounts and gone when it is left or reloaded - which is exactly the rule: a
 * reload is a new arrival, and the fold collects the line then. Persisting them
 * would keep a struck line standing after the reader chose to start again.
 *
 * Two things scope the whole database, and both DROP it when they change:
 *  - **the person**: a store keyed by the viewer's ref, so another person
 *    signing in on the same phone never inherits records or a queue (SYNC.md
 *    §7.4, "the queue is keyed by person id");
 *  - **the build id**: the record and row shapes are the build's, so a deploy
 *    invalidates them exactly as the build watchdog invalidates a stale tab.
 * The pair is the `identity` in `meta`; a mismatch clears every store.
 */

export interface StoredRecord {
  id: string;
  /** The record as it came off the wire (epoch-ms dates), plus whatever a
   *  superset row carried; read back through `live-store.ts`'s converters. */
  record: unknown;
  /** Last read or written, for the LRU bound. */
  touched: number;
}

export interface StoredSurface {
  key: string;
  /** The ids the surface last rendered - its membership, for the local first
   *  paint (SYNC.md §7.2) when the page has no server rows yet. */
  ids: string[];
  /** The last cursor the server sent with a push for this surface (opaque:
   *  never parsed, only handed back on the next `sub`). */
  cur: string | null;
}

/**
 * A GESTURE the device deferred (docs/RESOLUTION.md §1 step 1): the stimulus
 * itself, not what the device thought it meant. The meaning is recomputed at
 * arrival against the truth at arrival (§1 step 7), which is the whole point
 * of queueing the gesture rather than its commands - an act taken in a shop
 * lands as what it means when it gets there.
 *
 * The entry IS the `PendingStimulus`, its kind and its payload (an act's
 * target, a cancel's reason, a move's stage), with the envelope below beside
 * it (docs/LOADING.md slice 10, 2026-09-30). An entry stored before then holds
 * `kind: 'tick' | 'untick'` and no payload, which is those two kinds' own
 * shape, so it still replays as itself. The replay decodes whatever arrives
 * (`decodeQueuedGesture`, src/domain/resolution/wire.ts).
 */
export type StoredGesture = PendingStimulus & {
  /** The surface it was taken on - an argument, never a code path (§5). */
  surface: string;
  /** What the plan the device resolved expected the row's atoms to become, for
   *  the pending overlay while the gesture waits (docs/SYNC.md §7.3), and the
   *  replay's yardstick of the fold (§7.4). */
  expected: { delivered?: boolean; closed?: boolean; active?: boolean };
  /** The same expectation for every OTHER item the plan covered - a tick that
   *  swept its open sub-tasks (docs/RESOLUTION.md §6). One gesture still
   *  replays as one gesture, and its meaning is recomputed at arrival; this is
   *  only the overlay, so the whole set reads struck while the phone waits.
   *  Absent on an entry queued before the sweep existed. */
  swept?: Record<string, { delivered?: boolean; closed?: boolean; active?: boolean }>;
};

export interface StoredCommand {
  key: string;
  workItemId: string;
  /** Exactly one of these two. A raw COMMAND is what every surface that has not
   *  moved onto the resolver still defers; a GESTURE is the resolver's stimulus
   *  (the checklist tick, docs/RESOLUTION.md §12 step 3; any gesture of the
   *  item page since 2026-09-30). */
  command?: Command;
  gesture?: StoredGesture;
  occurredAt: number;
  basedOn: string | null;
  /** Device order: the counter at enqueue, so a replay keeps the order of the
   *  taps even when two commands share a millisecond. */
  seq: number;
}

interface LiveDb extends DBSchema {
  meta: { key: string; value: { key: string; identity: string } };
  records: { key: string; value: StoredRecord };
  surfaces: { key: string; value: StoredSurface };
  queue: { key: string; value: StoredCommand };
}

const DB_NAME = 'tesserano-live';
const DB_VERSION = 1;

let handle: Promise<IDBPDatabase<LiveDb> | null> | null = null;

/** The database, or null where IndexedDB is unavailable. Opened once. */
export function liveDb(): Promise<IDBPDatabase<LiveDb> | null> {
  if (handle) return handle;
  if (typeof indexedDB === 'undefined') {
    handle = Promise.resolve(null);
    return handle;
  }
  handle = openDB<LiveDb>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore('meta', { keyPath: 'key' });
      db.createObjectStore('records', { keyPath: 'id' });
      db.createObjectStore('surfaces', { keyPath: 'key' });
      db.createObjectStore('queue', { keyPath: 'key' });
    },
  }).catch(() => null);
  return handle;
}

/**
 * Adopt an identity (`<viewerRef>|<buildId>`), clearing every store when it
 * differs from the one the database holds. Returns whether the device kept its
 * contents (false = a different person or a different build, everything dropped).
 */
export async function adoptIdentity(identity: string): Promise<boolean> {
  const db = await liveDb();
  if (!db) return false;
  try {
    const held = await db.get('meta', 'identity');
    if (held?.identity === identity) return true;
    const tx = db.transaction(['meta', 'records', 'surfaces', 'queue'], 'readwrite');
    await Promise.all([
      tx.objectStore('records').clear(),
      tx.objectStore('surfaces').clear(),
      tx.objectStore('queue').clear(),
      tx.objectStore('meta').put({ key: 'identity', identity }),
      tx.done,
    ]);
    return false;
  } catch {
    return false;
  }
}

/** Every stored record, newest touch first. Empty where storage is unavailable. */
export async function loadRecords(): Promise<StoredRecord[]> {
  const db = await liveDb();
  if (!db) return [];
  try {
    const all = await db.getAll('records');
    return all.sort((a, b) => b.touched - a.touched);
  } catch {
    return [];
  }
}

/** Write records and delete the ids the LRU evicted, in one transaction. */
export async function persistRecords(puts: StoredRecord[], evicted: string[]): Promise<void> {
  if (puts.length === 0 && evicted.length === 0) return;
  const db = await liveDb();
  if (!db) return;
  try {
    const tx = db.transaction('records', 'readwrite');
    const store = tx.objectStore('records');
    const work: Promise<unknown>[] = [];
    for (const r of puts) work.push(store.put(r));
    for (const id of evicted) work.push(store.delete(id));
    work.push(tx.done);
    await Promise.all(work);
  } catch {
    // Best effort: the in-memory store already holds the truth.
  }
}

export async function loadSurfaces(): Promise<StoredSurface[]> {
  const db = await liveDb();
  if (!db) return [];
  try {
    return await db.getAll('surfaces');
  } catch {
    return [];
  }
}

export async function persistSurface(surface: StoredSurface): Promise<void> {
  const db = await liveDb();
  if (!db) return;
  try {
    await db.put('surfaces', surface);
  } catch {
    // Best effort.
  }
}

/** The queue in device order (`seq`). */
export async function loadQueue(): Promise<StoredCommand[]> {
  const db = await liveDb();
  if (!db) return [];
  try {
    const all = await db.getAll('queue');
    return all.sort((a, b) => a.seq - b.seq);
  } catch {
    return [];
  }
}

export async function persistQueue(puts: StoredCommand[], removed: string[]): Promise<void> {
  if (puts.length === 0 && removed.length === 0) return;
  const db = await liveDb();
  if (!db) return;
  try {
    const tx = db.transaction('queue', 'readwrite');
    const store = tx.objectStore('queue');
    const work: Promise<unknown>[] = [];
    for (const c of puts) work.push(store.put(c));
    for (const key of removed) work.push(store.delete(key));
    work.push(tx.done);
    await Promise.all(work);
  } catch {
    // Best effort.
  }
}
